import { classes } from "polytype";
import type {
  AppData,
  AppListItemData,
  AppListQueryData,
  AppName,
} from "./types.js";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import assertObjectFound from "../../base/assertObjectFound.js";
import { language } from "../../base/i18n/setup.js";
import type { AppVersionListQuery } from "../AppVersion/index.js";
import { AppVersion } from "../AppVersion/index.js";

export class App extends ReferenceModel {
  public readonly recommendedVersions: AppVersionListQuery;

  public constructor(id: string) {
    super(id);
    this.recommendedVersions = AppVersion.query(this, {
      recommended: true,
    });
  }

  public static ofId(id: string) {
    return new App(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.app.find(id);
    if (data) {
      return new AppDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const app = await this.find(id);
    assertObjectFound(app, "app", id);
    return app;
  });

  public findDetailed = provideReact(
    () => App.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<AppDetailed | undefined>>;

  public getDetailed = provideReact(
    () => App.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<AppDetailed>>;

  public static query = (query: AppListQueryData = {}) =>
    new AppListQuery(query);
}

class AppCommon extends classes(DataModel<AppData | AppListItemData>, App) {
  public readonly name: AppName;
  public readonly isCustomApp: boolean;
  public readonly isNodeApp: boolean;
  public readonly translatedDescription: string;
  public readonly tags: string[];
  public readonly isPopular: boolean;
  public constructor(data: AppData | AppListItemData) {
    super([data], [data.id]);
    this.name = data.name as AppName;
    this.isNodeApp = this.name === "Node.js";
    this.isCustomApp =
      this.isNodeApp ||
      this.name === "PHP" ||
      this.name === "Static Files" ||
      this.name === "Python" ||
      this.name === "PHP-Worker";
    this.translatedDescription = language.default.app.description[this.name];
    this.tags = data.tags;
    this.isPopular =
      this.name === "WordPress" ||
      this.name === "TYPO3" ||
      this.name === "Shopware 6" ||
      this.name === "PHP";
  }
}

export class AppDetailed extends classes(AppCommon, DataModel<AppData>) {
  public constructor(data: AppData) {
    super([data], [data]);
  }
}

export class AppListItem extends classes(
  AppCommon,
  DataModel<AppListItemData>,
) {
  public constructor(data: AppListItemData) {
    super([data], [data]);
  }
}

export class AppListQuery extends ListQueryModel<AppListQueryData> {
  public constructor(query: AppListQueryData = {}) {
    super(query);
  }

  public refine(query: AppListQueryData) {
    return new AppListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.app.list(this.query);

    return new AppList(
      this.query,
      items.map((d) => new AppListItem(d)),
    );
  }, [this.queryId, "app"]);
}

export class AppList extends classes(AppListQuery, ListDataModel<AppListItem>) {
  public constructor(query: AppListQueryData, apps: AppListItem[]) {
    super([query], [apps, apps.length]);
  }
}
